/**
 * Product Card Component
 * 
 * Displays product information with lazy loading, image optimization,
 * and cart integration. Supports product variants and responsive design.
 */

import React, { useState, useCallback, memo } from 'react';
import { Product, ProductVariant } from '../../types/ecommerce';
import { useCartStore, cartSelectors } from '../../store/simpleCartStore';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';

/**
 * Product card props interface
 */
interface ProductCardProps {
  /** Product data to display */
  product: Product;
  /** Card variant style */
  variant?: 'grid' | 'list' | 'featured';
  /** Whether to show quick add to cart button */
  showQuickAdd?: boolean;
  /** Whether to show product variants */
  showVariants?: boolean;
  /** Click handler for product card */
  onClick?: (product: Product) => void;
  /** Custom CSS classes */
  className?: string;
  /** Whether images should lazy load */
  lazyLoad?: boolean;
  /** Priority loading for above-the-fold products */
  priority?: boolean;
}

/**
 * Lazy loaded image component with fallback
 */
interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = memo(({ 
  src, 
  alt, 
  className = '', 
  priority = false,
  onLoad,
  onError 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  if (hasError) {
    return (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

/**
 * Product variant selector component
 */
interface VariantSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  onVariantChange: (variant: ProductVariant) => void;
}

const VariantSelector: React.FC<VariantSelectorProps> = memo(({ 
  variants, 
  selectedVariant, 
  onVariantChange 
}) => {
  if (variants.length === 0) return null;

  return (
    <div className="mt-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Options
      </label>
      <select
        value={selectedVariant?.id || ''}
        onChange={(e) => {
          const variant = variants.find(v => v.id === e.target.value);
          if (variant) onVariantChange(variant);
        }}
        className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="">Select option</option>
        {variants.map((variant) => (
          <option key={variant.id} value={variant.id}>
            {Object.entries(variant.attributes).map(([key, value]) => 
              `${key}: ${value}`
            ).join(', ')} - ${variant.price > 0 ? `$${variant.price.toFixed(2)}` : 'Free'}
          </option>
        ))}
      </select>
    </div>
  );
});

VariantSelector.displayName = 'VariantSelector';

/**
 * Product Card Component
 * 
 * Optimized for performance with memoization and lazy loading.
 * Integrates with cart store for seamless shopping experience.
 */
export const ProductCard: React.FC<ProductCardProps> = memo(({
  product,
  variant = 'grid',
  showQuickAdd = true,
  showVariants = false,
  onClick,
  className = '',
  priority = false,
}) => {
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  
  // Cart store integration
  const addItem = useCartStore((state) => state.addItem);
  const isAddingItem = useCartStore(cartSelectors.isAddingItem);
  const isItemInCart = useCartStore((state) => state.isItemInCart);
  const cartItem = useCartStore((state) => state.getCartItem);

  // Get primary product image
  const primaryImage = product.images[0];
  const imageUrl = primaryImage?.src || '/placeholder-product.jpg';
  const imageAlt = primaryImage?.alt || product.name;

  // Calculate display price
  const displayPrice = selectedVariant?.price || product.price;
  const originalPrice = product.regularPrice && product.regularPrice > displayPrice 
    ? product.regularPrice 
    : null;

  // Stock status
  const isInStock = product.inventory.stockStatus === 'instock';
  const {stockQuantity} = product.inventory;
  const isLowStock = stockQuantity > 0 && stockQuantity <= 5;

  // Check if item is already in cart
  const inCart = isItemInCart(product.id, selectedVariant?.id);
  const existingCartItem = cartItem(product.id, selectedVariant?.id);

  /**
   * Handle add to cart action
   */
  const handleAddToCart = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!isInStock) return;
    
    try {
      await addItem(product, 1, selectedVariant || undefined);
    } catch (error) {
      console.error('Failed to add item to cart:', error);
    }
  }, [addItem, product, selectedVariant, isInStock]);

  /**
   * Handle product card click
   */
  const handleCardClick = useCallback(() => {
    onClick?.(product);
  }, [onClick, product]);

  /**
   * Handle variant selection
   */
  const handleVariantChange = useCallback((variant: ProductVariant) => {
    setSelectedVariant(variant);
  }, []);

  /**
   * Render price section
   */
  const renderPrice = () => (
    <div className="flex items-center gap-2">
      <span className="text-lg font-bold text-gray-900">
        ${displayPrice.toFixed(2)}
      </span>
      {originalPrice && (
        <span className="text-sm text-gray-500 line-through">
          ${originalPrice.toFixed(2)}
        </span>
      )}
      {product.salePrice && (
        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
          Sale
        </span>
      )}
    </div>
  );

  /**
   * Render stock status
   */
  const renderStockStatus = () => {
    if (!isInStock) {
      return (
        <span className="text-sm text-red-600 font-medium">
          Out of Stock
        </span>
      );
    }
    
    if (isLowStock) {
      return (
        <span className="text-sm text-orange-600 font-medium">
          Only {stockQuantity} left!
        </span>
      );
    }
    
    return (
      <span className="text-sm text-green-600 font-medium">
        In Stock
      </span>
    );
  };

  /**
   * Render add to cart section
   */
  const renderAddToCart = () => {
    if (!showQuickAdd) return null;

    const buttonText = inCart 
      ? `In Cart (${existingCartItem?.quantity || 0})` 
      : 'Add to Cart';

    return (
      <Button
        onClick={handleAddToCart}
        disabled={!isInStock || isAddingItem}
        variant={inCart ? 'secondary' : 'primary'}
        size="sm"
        className="w-full mt-2"
      >
        {isAddingItem ? (
          <div className="flex items-center justify-center gap-2">
            <LoadingSpinner size="xs" />
            Adding...
          </div>
        ) : (
          buttonText
        )}
      </Button>
    );
  };

  // Variant-specific classes
  const cardClasses = {
    grid: 'bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer',
    list: 'bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer flex',
    featured: 'bg-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-1',
  };

  const imageClasses = {
    grid: 'w-full h-48 sm:h-56',
    list: 'w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0',
    featured: 'w-full h-64 sm:h-72',
  };

  const contentClasses = {
    grid: 'p-4',
    list: 'flex-1 p-4 flex flex-col justify-between',
    featured: 'p-6',
  };

  return (
    <article 
      className={`${cardClasses[variant]} ${className}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Product Image */}
      <div className={imageClasses[variant]}>
        <LazyImage
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full"
          priority={priority}
          onLoad={() => {}}
        />
        
        {/* Featured badge */}
        {product.featured && (
          <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-medium">
            Featured
          </div>
        )}
        
        {/* Sale badge */}
        {product.salePrice && (
          <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
            Sale
          </div>
        )}
      </div>

      {/* Product Content */}
      <div className={contentClasses[variant]}>
        {/* Product Name */}
        <h3 className={`font-semibold text-gray-900 mb-2 ${
          variant === 'featured' ? 'text-xl' : 'text-lg'
        }`}>
          {product.name}
        </h3>

        {/* Product Description (for featured variant) */}
        {variant === 'featured' && product.shortDescription && (
          <p className="text-gray-600 mb-3 line-clamp-2">
            {product.shortDescription.replace(/<[^>]*>/g, '')}
          </p>
        )}

        {/* Price */}
        {renderPrice()}

        {/* Stock Status */}
        <div className="mt-2">
          {renderStockStatus()}
        </div>

        {/* Variant Selector */}
        {showVariants && product.variants && product.variants.length > 0 && (
          <VariantSelector
            variants={product.variants}
            selectedVariant={selectedVariant}
            onVariantChange={handleVariantChange}
          />
        )}

        {/* Add to Cart Button */}
        {renderAddToCart()}

        {/* Product Categories (for featured variant) */}
        {variant === 'featured' && product.categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {product.categories.slice(0, 3).map((category) => (
              <span
                key={category.id}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

ProductCard.displayName = 'ProductCard';